import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  VpnKey as VpnKeyIcon,
} from '@mui/icons-material';
import { contadorAccessService, ContadorAdmin } from '../services';
import { useToast } from '../hook/use-toast';

const ContadoresAccessPage: React.FC = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState<ContadorAdmin[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const [passwordDialog, setPasswordDialog] = useState<{ id: string; email: string } | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [deleteDialog, setDeleteDialog] = useState<ContadorAdmin | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await contadorAccessService.list();
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Erro ao carregar acessos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleCreate = async () => {
    if (!name.trim() || !email.trim() || password.length < 6) {
      toast({
        title: 'Preencha nome, email e senha (mín. 6 caracteres)',
        variant: 'destructive',
      });
      return;
    }
    setSaving(true);
    try {
      await contadorAccessService.create({
        name: name.trim(),
        email: email.trim(),
        password,
      });
      toast({ title: 'Acesso do contador criado' });
      setCreateOpen(false);
      setName('');
      setEmail('');
      setPassword('');
      await load();
    } catch (err: any) {
      toast({
        title: err?.response?.data?.message || 'Erro ao criar acesso',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (item: ContadorAdmin) => {
    try {
      await contadorAccessService.update(item._id, { active: !item.active });
      toast({ title: item.active ? 'Acesso desativado' : 'Acesso ativado' });
      await load();
    } catch (err: any) {
      toast({
        title: err?.response?.data?.message || 'Erro ao atualizar',
        variant: 'destructive',
      });
    }
  };

  const handleResetPassword = async () => {
    if (!passwordDialog || newPassword.length < 6) {
      toast({ title: 'Senha deve ter no mínimo 6 caracteres', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      await contadorAccessService.update(passwordDialog.id, { password: newPassword });
      toast({ title: 'Senha atualizada' });
      setPasswordDialog(null);
      setNewPassword('');
    } catch (err: any) {
      toast({
        title: err?.response?.data?.message || 'Erro ao redefinir senha',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog) return;
    setSaving(true);
    try {
      await contadorAccessService.remove(deleteDialog._id);
      toast({ title: 'Acesso excluído' });
      setDeleteDialog(null);
      await load();
    } catch (err: any) {
      toast({
        title: err?.response?.data?.message || 'Erro ao excluir',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2} mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Acesso do Contador
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Crie um login (email e senha) para o contador. Link de acesso:{' '}
            <strong>/contador</strong> (ex.: http://admin.localhost:3001/contador).
            Ele só verá a aba de Emissão de Nota com valores e dados fiscais.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button startIcon={<RefreshIcon />} onClick={() => void load()} disabled={loading}>
            Atualizar
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
            Novo acesso
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper>
        {loading ? (
          <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Último login</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      Nenhum acesso de contador criado ainda.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                items.map((item) => (
                  <TableRow key={item._id} hover>
                    <TableCell>{item.name}</TableCell>
                    <TableCell>{item.email}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={item.active ? 'Ativo' : 'Inativo'}
                        color={item.active ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      {item.lastLogin
                        ? new Date(item.lastLogin).toLocaleString('pt-BR')
                        : '—'}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        title="Redefinir senha"
                        onClick={() => {
                          setPasswordDialog({ id: item._id, email: item.email });
                          setNewPassword('');
                        }}
                      >
                        <VpnKeyIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        title="Excluir"
                        color="error"
                        onClick={() => setDeleteDialog(item)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                      <Switch
                        size="small"
                        checked={item.active}
                        onChange={() => void handleToggleActive(item)}
                        inputProps={{ 'aria-label': 'Ativar/desativar' }}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Dialog open={createOpen} onClose={() => !saving && setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Novo login do contador</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Nome"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              autoFocus
            />
            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
            />
            <TextField
              label="Senha"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              helperText="Mínimo 6 caracteres"
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={() => void handleCreate()} disabled={saving}>
            {saving ? 'Salvando…' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(passwordDialog)}
        onClose={() => !saving && setPasswordDialog(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Redefinir senha</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {passwordDialog?.email}
          </Typography>
          <TextField
            label="Nova senha"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            fullWidth
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialog(null)} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={() => void handleResetPassword()} disabled={saving}>
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deleteDialog)}
        onClose={() => !saving && setDeleteDialog(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Excluir acesso</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Excluir o acesso de <strong>{deleteDialog?.name}</strong> ({deleteDialog?.email})?
            Essa ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(null)} disabled={saving}>
            Cancelar
          </Button>
          <Button color="error" variant="contained" onClick={() => void handleDelete()} disabled={saving}>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ContadoresAccessPage;
